import React, { useState, MouseEvent } from 'react';
import { Select } from '@components/Select';
import { RadioButton } from '@components/RadioButton';
import { setURLfromFilters, getNameFromSubList, getFiltersFromURL } from './NewsFilter.helpers';
import './NewsFilter.scss';
import { NewsListFilter } from '@pages/NewsList/NewsList.types';
import { CountryItem, IndustryItem } from '@type/common';

type NewsFilterItems = Omit<NewsListFilter, 'page'>;
interface NewsFilterProps {
  i18n: Record<string, string>;
  onSaveFilter: (filters: NewsFilterItems) => void;
  categories: Record<string, number>;
  industriesList: IndustryItem[];
  countriesList: CountryItem[];
  isLoadingNews: boolean;
}

const defaultFilters: NewsFilterItems = {
  industries: '',
  country: '',
  category: '',
  startDate: '',
  endDate: '',
};

export const NewsFilter = ({
  i18n,
  onSaveFilter,
  categories,
  industriesList,
  countriesList,
  isLoadingNews,
}: NewsFilterProps) => {
  const [filters, setFilters] = useState<Omit<NewsListFilter, 'page'>>(getFiltersFromURL());
  const dateError = Date.parse(filters.endDate) < Date.parse(filters.startDate);

  const doResetFilters = () => {
    setFilters(defaultFilters);
    setURLfromFilters(defaultFilters);
  };

  const setFilter = (key: string, val: string | number) => {
    const newFilters = { ...filters };

    newFilters[key] = val;

    setURLfromFilters(newFilters);
    setFilters(newFilters);
  };

  const handleUpdateClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onSaveFilter(filters);
  };

  return (
    <div className='c-news-filter'>
      <form>
        {categories && (
          <div className='c-news-filter-row'>
            <fieldset>
              <legend className='c-news-filter-label'>{i18n.CategoryTitle}</legend>
              <RadioButton
                title={i18n.AllCategory}
                onChange={() => setFilter('category', '')}
                id='all_radio'
                name='category'
                checked={filters.category === ''}
              />
              {Object.keys(categories).map((category) => (
                <RadioButton
                  key={categories[category]}
                  title={category}
                  id={categories[category].toString()}
                  onChange={() => {
                    setFilter('category', categories[category]);
                  }}
                  name='category'
                  checked={parseInt(filters.category) === categories[category]}
                />
              ))}
            </fieldset>
          </div>
        )}

        {countriesList && (
          <div className='c-news-filter-row'>
            <fieldset data-country-picker='' className=''>
              <label className='c-news-filter-label'>{i18n.CountryTitle}</label>
              <Select
                title={
                  filters.country
                    ? getNameFromSubList(filters.country, countriesList, 'Countries')
                    : i18n.ShowAllRegions
                }
                defaultTitle={i18n.ShowAllRegions}
                selectableTitle={true}
                list={countriesList}
                name='Name'
                sub='Countries'
                selectFunction={(value) => setFilter('country', value?.Id ? value.Id : '')}
                backLabel={i18n.Back}
              />
            </fieldset>
          </div>
        )}

        <div className='c-news-filter-row'>
          <fieldset>
            <legend className='c-news-filter-label'>{i18n.IndustryTitle}</legend>
            <Select
              title={
                filters.industries
                  ? getNameFromSubList(filters.industries, industriesList, 'SubIndustries')
                  : i18n.AllIndustry
              }
              defaultTitle={i18n.AllIndustry}
              selectableTitle={true}
              list={industriesList}
              name='Name'
              sub='SubIndustries'
              selectFunction={(value) => setFilter('industries', value?.Id ? value.Id : '')}
              backLabel={i18n.Back}
            />
          </fieldset>
        </div>

        <div className='c-news-filter-row'>
          <div className='c-news-filter-row-50'>
            <span className='c-news-filter-label'>{i18n.StartDateTitle}</span>
            <input
              onChange={(e) => setFilter('startDate', e.currentTarget.value)}
              className={dateError ? 'c-input-date c-news-filter-error-border' : 'c-input-date'}
              name='startDate'
              type='date'
              id='startDate'
              value={filters.startDate}
            />
          </div>
          <div className='c-news-filter-row-50'>
            <span className='c-news-filter-label'>{i18n.EndDateTitle}</span>
            <input
              onChange={(e) => setFilter('endDate', e.currentTarget.value)}
              className={dateError ? 'c-input-date c-news-filter-error-border' : 'c-input-date'}
              name='endDate'
              type='date'
              id='endDate'
              value={filters.endDate}
            />
          </div>

          {dateError && <div className='c-news-filter-error-message'>{i18n.DateValidation}</div>}
        </div>
        <div className='c-news-filter-row'>
          <fieldset className='fieldset'>
            <button
              disabled={isLoadingNews}
              onClick={handleUpdateClick}
              className='c-news-filter-button c-button is-primary'
            >
              {i18n.UpdateButtonText}
            </button>
            <a onClick={doResetFilters} className='c-news-filter-button c-button is-default reset'>
              {i18n.ResetFilterButtonText}
            </a>
          </fieldset>
        </div>
      </form>
    </div>
  );
};
